/**
 * calDateSlotsSort：slotID 一维 string[]
 */

import type { CalDateInfo, SlotInfo, SlotType, ShiftType } from '../types/schedule.types';
import { SlotType as SlotTypeEnum } from '../types/schedule.types';

function isNonEmptySoltId(s: unknown): s is string {
  return typeof s === 'string' && s.length > 0;
}

/** 规范为 slotID 一维数组（保持顺序） */
export function normalizeToSortedIds(sort: string[] | undefined | null): string[] {
  if (!sort || !Array.isArray(sort)) return [];
  return sort.filter(isNonEmptySoltId);
}

/** 展平排序 ID（同 normalizeToSortedIds） */
export function flatten(sort: string[] | undefined | null): string[] {
  return normalizeToSortedIds(sort);
}

export const SHIFT_ORDER: ShiftType[] = [
  'MORNING',
  'AFTERNOON',
  'EVENING',
  'APPENDIX1',
  'APPENDIX2',
  'APPENDIX3',
  'APPENDIX4',
  'APPENDIX5',
];

export type LocationHeaderVisibilityOptions = {
  /**
   * UI 需要“显示全部地点”时，强制认为每条 SHOOT 都显示地点头；
   * 仅影响渲染/分组，不会回写 slotLocationVisibility。
   */
  forceShowAllLocations?: boolean;
};

/** Y/缺省=显示地点头；N=隐藏（归入上一虚拟段） */
export function slotShowsLocationHeader(
  slot: SlotInfo | undefined,
  options?: LocationHeaderVisibilityOptions,
): boolean {
  if (!slot) return true;
  if (slot.slotType === SlotTypeEnum.NOTE) return false;
  if (options?.forceShowAllLocations) return true;
  const v = slot.slotLocationVisibility;
  if (v === undefined || v === null || String(v).trim() === '') return true;
  return String(v).toUpperCase() === 'Y';
}

/** 从扁平顺序中某 SHOOT 锚点起，连续归入同虚拟段的任务 ID（不含下一个 NOTE 或下一个「显式头」SHOOT） */
export function getVirtualSoltBundle(
  sortedIds: string[],
  anchorSoltId: string,
  slotMap: Map<string, SlotInfo>,
  options?: LocationHeaderVisibilityOptions,
): string[] {
  const start = sortedIds.indexOf(anchorSoltId);
  if (start === -1) return [anchorSoltId];
  const anchor = slotMap.get(anchorSoltId);
  if (!anchor || anchor.slotType === SlotTypeEnum.NOTE) {
    return [anchorSoltId];
  }
  if (options?.forceShowAllLocations) return [anchorSoltId];
  const bundle: string[] = [];
  for (let i = start; i < sortedIds.length; i++) {
    const id = sortedIds[i];
    const t = slotMap.get(id);
    if (!t) {
      bundle.push(id);
      continue;
    }
    if (t.slotType === SlotTypeEnum.NOTE) break;
    if (i > start && slotShowsLocationHeader(t, options)) break;
    bundle.push(id);
  }
  return bundle.length > 0 ? bundle : [anchorSoltId];
}

export interface SlotForGrouping {
  slotID: string;
  slotType?: SlotType;
  slotBelongSceneID?: string;
  slotShift?: ShiftType | null;
  slotLocationVisibility?: string | null;
}

export type DisplaySegment =
  | {
      kind: 'note';
      groupId: string;
      items: string[];
    }
  | {
      kind: 'slotSegment';
      groupId: string;
      items: string[];
      showHeader: boolean;
      shift?: ShiftType;
      locationLabel?: string;
    };

export type GetSceneInfoForDisplay = (sceneID: string | undefined) =>
  | { sceneLocation?: string }
  | undefined;

function isNoteSolt(slot: SlotInfo | undefined): boolean {
  return !!slot && (slot.slotType === SlotTypeEnum.NOTE || !slot.slotType);
}

/**
 * 由扁平 ID 顺序 + 当日任务 构建日历展示段（便签每段一个；SHOOT 按 visibility 切虚拟段）
 */
export function buildDisplaySegments(
  slots: SlotForGrouping[],
  calDateSlotsSort: string[] | undefined | null,
  dateStr: string,
  getSceneInfo: GetSceneInfoForDisplay,
  options?: LocationHeaderVisibilityOptions,
): DisplaySegment[] {
  const slotMap = new Map(slots.map((t) => [t.slotID, t as SlotInfo]));
  const ids = normalizeToSortedIds(calDateSlotsSort);
  const segments: DisplaySegment[] = [];
  const processedFromSort = new Set<string>();
  let i = 0;

  while (i < ids.length) {
    const id = ids[i];
    const slot = slotMap.get(id);
    if (!slot) {
      i++;
      continue;
    }

    if (isNoteSolt(slot)) {
      segments.push({
        kind: 'note',
        groupId: `${dateStr}__note-${id}`,
        items: [id],
      });
      processedFromSort.add(id);
      i++;
      continue;
    }

    const bundle: string[] = [];
    const showHeader = slotShowsLocationHeader(slot, options);
    while (i < ids.length) {
      const tid = ids[i];
      const t = slotMap.get(tid);
      if (!t || isNoteSolt(t)) break;
      if (bundle.length > 0 && slotShowsLocationHeader(t, options)) break;
      bundle.push(tid);
      processedFromSort.add(tid);
      i++;
    }
    const anchor = slotMap.get(bundle[0]);
    let anchorShift: ShiftType | undefined;
    let locationLabel: string | undefined;
    if (anchor) {
      anchorShift = (anchor.slotShift ?? undefined) as ShiftType | undefined;
      const scene = getSceneInfo(anchor.slotBelongSceneID);
      locationLabel = scene?.sceneLocation ?? '';
    }
    segments.push({
      kind: 'slotSegment',
      groupId: `${dateStr}__segment-${bundle[0]}`,
      items: bundle,
      showHeader,
      shift: anchorShift,
      locationLabel,
    });
  }

  const processed = processedFromSort;
  const orphans = slots.filter((t) => !processed.has(t.slotID));
  for (const t of orphans) {
    const pt = t as SlotInfo;
    if (isNoteSolt(pt)) {
      segments.push({
        kind: 'note',
        groupId: `${dateStr}__note-${pt.slotID}`,
        items: [pt.slotID],
      });
    } else {
      const scene = getSceneInfo(pt.slotBelongSceneID);
      segments.push({
        kind: 'slotSegment',
        groupId: `${dateStr}__segment-${pt.slotID}`,
        items: [pt.slotID],
        showHeader: slotShowsLocationHeader(pt, options),
        shift: (pt.slotShift ?? undefined) as ShiftType | undefined,
        locationLabel: scene?.sceneLocation ?? '',
      });
    }
  }

  return segments;
}

/**
 * 与日历 DayInfo 列表行顺序一致的 slotID 序列（每行一条，含 NOTE / 独立 SHOOT）
 */
export function getCalendarDayRowOrderedIds(
  slots: SlotInfo[],
  calDateSlotsSort: string[] | undefined | null,
  dateStr: string,
  getSceneInfo: GetSceneInfoForDisplay,
): string[] {
  const daySolts = slots.filter((t) => {
    if (!t.slotDate) return false;
    if (t.slotDate.split('T')[0] !== dateStr) return false;
    return (
      t.slotType === SlotTypeEnum.NOTE ||
      t.slotType === SlotTypeEnum.SHOOT ||
      !t.slotType
    );
  });
  const slotMap = new Map(daySolts.map((t) => [t.slotID, t]));
  const segments = buildDisplaySegments(daySolts, calDateSlotsSort, dateStr, getSceneInfo);
  const ids: string[] = [];
  for (const seg of segments) {
    for (const id of seg.items) {
      if (slotMap.has(id)) ids.push(id);
    }
  }
  return ids;
}

export function buildGroupedDisplayGroups(
  slots: SlotForGrouping[],
  calDateSlotsSort: string[] | undefined | null,
  dateStr: string,
  getSceneInfo: GetSceneInfoForDisplay,
): Array<{
  groupId: string;
  items: string[];
  /** 与剪贴板一致：SHOOTS = 同日历一段连续「虚拟地点头」下的 SHOOT 行，NOTE = 便签 */
  type: 'SHOOTS' | 'NOTE';
  location?: string;
  shift?: ShiftType;
  showHeader?: boolean;
}> {
  const segs = buildDisplaySegments(slots, calDateSlotsSort, dateStr, getSceneInfo);
  return segs.map((s) => {
    if (s.kind === 'note') {
      return {
        groupId: s.groupId,
        items: s.items,
        type: 'NOTE' as const,
        showHeader: false,
      };
    }
    return {
      groupId: s.groupId,
      items: s.items,
      type: 'SHOOTS' as const,
      location: s.locationLabel,
      shift: s.shift,
      showHeader: s.showHeader,
    };
  });
}

export function mergeCalDateShift(
  targetShift: string | undefined | null,
  sourceShift: string | undefined | null,
): string | undefined {
  if (targetShift != null && String(targetShift).trim() !== '') {
    return String(targetShift).trim();
  }
  if (sourceShift != null && String(sourceShift).trim() !== '') {
    return String(sourceShift).trim();
  }
  return undefined;
}

export function mergeSortIds(existing: string[] | undefined, incoming: string[] | undefined): string[] {
  const a = normalizeToSortedIds(existing);
  const b = normalizeToSortedIds(incoming);
  const seen = new Set(a);
  const out = [...a];
  for (const id of b) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

export function getGroupShiftsFromSolts(daySolts: SlotInfo[]): ShiftType[] {
  const shiftOrder = SHIFT_ORDER;
  const seen = new Set<ShiftType>();
  const result: ShiftType[] = [];
  for (const shift of shiftOrder) {
    const hasShift = daySolts.some(
      (t) =>
        (t.slotType === SlotTypeEnum.SHOOT || !t.slotType) &&
        t.slotShift === shift,
    );
    if (hasShift && !seen.has(shift)) {
      seen.add(shift);
      result.push(shift);
    }
  }
  return result;
}

/** 存储层是否视为「显示地点头」（空/缺省同 Y） */
function locationVisibilityStoredMeansY(v: string | null | undefined): boolean {
  if (v === undefined || v === null || String(v).trim() === '') return true;
  return String(v).toUpperCase() === 'Y';
}

function locationVisibilityStoredIsN(v: string | null | undefined): boolean {
  return String(v ?? '').toUpperCase() === 'N';
}

function isNoteSlotForLocationDedupe(slot: SlotInfo): boolean {
  return slot.slotType === SlotTypeEnum.NOTE;
}

export type SlotLocationVisibilityPatch = {
  slotID: string;
  slotLocationVisibility: 'Y' | 'N';
  /** 与同地点组内首条对齐时传入（含 null 表示清空）；不传则不修改班次 */
  slotShift?: ShiftType | null;
};

function normalizedSlotShiftForDedupe(slot: SlotInfo): ShiftType | null {
  const s = slot.slotShift;
  if (s === undefined || s === null) return null;
  return s;
}

/**
 * 按每个 calDate 的 calDateSlotsSort 顺序，比较相邻 SHOOT 的 scene 地点（由 getSceneLocation 提供）；
 * 连续相同则首条为 Y、其余为 N；便签打断连续段。仅返回相对当前 slot 状态需写入的项。
 *
 * 顺序与日历 DayInfo 一致：通过 buildDisplaySegments 生成线性 ID 列表（含 sort 为空或未列全时的孤儿任务顺序）。
 * 仅处理已有 calDate 记录的计划日，不兜底「无 calDate 但有任务」的日期。
 */
function isRowShootSlot(slot: SlotInfo | undefined): boolean {
  return !!slot && (slot.slotType === SlotTypeEnum.SHOOT || !slot.slotType);
}

/**
 * 与日历行一致：同址判断仅依据场次 sceneLocation。
 */
export function effectiveSlotRowLocationKey(
  slot: SlotInfo | undefined,
  getSceneInfo: GetSceneInfoForDisplay,
): string {
  if (!slot || !isRowShootSlot(slot)) return '';
  return (getSceneInfo(slot.slotBelongSceneID)?.sceneLocation ?? '').trim().toLowerCase();
}

/**
 * 移动块落位后：若紧挨在块后的第一条 SHOOT 与块内最后一条 SHOOT 的展示地点不同，
 * 则返回应将 slotLocationVisibility 设为 Y 的 follower slotID；否则 null。
 */
export function getFollowerSoltIdForLocationVisibilityYAfterMove(
  newOrderedIds: string[],
  movedBlockOrderedIds: string[],
  slotMap: Map<string, SlotInfo>,
  getSceneInfo: GetSceneInfoForDisplay,
): string | null {
  if (movedBlockOrderedIds.length === 0) return null;
  const lastMovedId = movedBlockOrderedIds[movedBlockOrderedIds.length - 1];
  const lastIdx = newOrderedIds.indexOf(lastMovedId);
  if (lastIdx === -1 || lastIdx >= newOrderedIds.length - 1) return null;
  const followId = newOrderedIds[lastIdx + 1];
  const lastSolt = slotMap.get(lastMovedId);
  const followSolt = slotMap.get(followId);
  if (!isRowShootSlot(lastSolt) || !isRowShootSlot(followSolt)) return null;
  if (effectiveSlotRowLocationKey(lastSolt, getSceneInfo) === effectiveSlotRowLocationKey(followSolt, getSceneInfo)) {
    return null;
  }
  return followId;
}

/**
 * 源日排序中，因目标日重复而从源日 **删除** 的 SHOOT（地点头为 Y/缺省）之后：
 * 跳过连续的「同批删除」项，若下一条 SHOOT 仍为同展示地点且 slotLocationVisibility 为 N，应改为 Y。
 * ※ `idsToDelete` 仅含删重移除的 id，不含仅跨日移动的 id（否则会把仍在源日的 N 误抬升）。
 */
export function getFollowerSoltIdsNeedingYAfterDuplicateDeletesForLocationVisibility(
  oldOrderedIds: string[],
  idsToDelete: Set<string> | string[],
  slotMap: Map<string, SlotInfo>,
  getSceneInfo: GetSceneInfoForDisplay,
): string[] {
  const delSet = idsToDelete instanceof Set ? idsToDelete : new Set(idsToDelete);
  if (delSet.size === 0 || oldOrderedIds.length === 0) return [];

  const out: string[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < oldOrderedIds.length; i++) {
    const rid = oldOrderedIds[i];
    if (!delSet.has(rid)) continue;
    const rt = slotMap.get(rid);
    if (!isRowShootSlot(rt)) continue;
    if (!locationVisibilityStoredMeansY(rt!.slotLocationVisibility)) continue;

    let j = i + 1;
    while (j < oldOrderedIds.length && delSet.has(oldOrderedIds[j])) j++;
    if (j >= oldOrderedIds.length) continue;

    const fid = oldOrderedIds[j];
    const ft = slotMap.get(fid);
    if (ft === undefined || !isRowShootSlot(ft)) continue;
    if (!locationVisibilityStoredIsN(ft.slotLocationVisibility)) continue;

    const addrR = effectiveSlotRowLocationKey(rt, getSceneInfo);
    const addrF = effectiveSlotRowLocationKey(ft, getSceneInfo);
    if (!addrR || addrR !== addrF) continue;

    if (!seen.has(fid)) {
      seen.add(fid);
      out.push(fid);
    }
  }
  return out;
}

/**
 * 从某日移除若干行（拖拽走或删重）后：若被移除的 SHOOT 中存在 visibility 为 Y/缺省（显示地点头）的项，
 * 且在该日 **原顺序** 中位于「最下方被移除行」正下方的 SHOOT 与其同址且当前为 N，则应将该 follower 改为 Y。
 * `removedFromSource` 须为从该日拍摄计划中去掉的所有 slotID（含跨日时实际移动与因目标重复被删除的项）。
 */
export function getSourceFollowerSoltIdNeedingLocationVisibilityYAfterRemove(
  oldOrderedIds: string[],
  removedFromSource: Set<string> | string[],
  slotMap: Map<string, SlotInfo>,
  getSceneInfo: GetSceneInfoForDisplay,
): string | null {
  const removedSet = removedFromSource instanceof Set ? removedFromSource : new Set(removedFromSource);
  if (removedSet.size === 0 || oldOrderedIds.length === 0) return null;

  const removedIdArr = Array.from(removedSet);
  const hasVisibleLocationHeaderInRemoved = removedIdArr.some((id) => {
    const t = slotMap.get(id);
    if (!isRowShootSlot(t)) return false;
    return locationVisibilityStoredMeansY(t!.slotLocationVisibility);
  });
  if (!hasVisibleLocationHeaderInRemoved) return null;

  let maxRemovedIdx = -1;
  for (let r = 0; r < removedIdArr.length; r++) {
    const id = removedIdArr[r];
    const i = oldOrderedIds.indexOf(id);
    if (i > maxRemovedIdx) maxRemovedIdx = i;
  }
  if (maxRemovedIdx === -1 || maxRemovedIdx >= oldOrderedIds.length - 1) return null;

  const followerId = oldOrderedIds[maxRemovedIdx + 1];
  if (removedSet.has(followerId)) return null;

  const followerSolt = slotMap.get(followerId);
  if (followerSolt === undefined || !isRowShootSlot(followerSolt)) return null;
  if (!locationVisibilityStoredIsN(followerSolt.slotLocationVisibility)) return null;

  const bottomRemovedId = oldOrderedIds[maxRemovedIdx];
  const bottomRemoved = slotMap.get(bottomRemovedId);
  if (bottomRemoved === undefined || !isRowShootSlot(bottomRemoved)) return null;

  const addrRemoved = effectiveSlotRowLocationKey(bottomRemoved, getSceneInfo);
  const addrFollow = effectiveSlotRowLocationKey(followerSolt, getSceneInfo);
  if (!addrRemoved || addrRemoved !== addrFollow) return null;

  return followerId;
}

function filterDaySoltsForCalendar(
  slotsForSchedule: SlotInfo[],
  dateStr: string,
): SlotInfo[] {
  return slotsForSchedule.filter((t) => {
    if (!t.slotDate) return false;
    const ds = t.slotDate.split('T')[0];
    if (ds !== dateStr) return false;
    return (
      t.slotType === SlotTypeEnum.NOTE ||
      t.slotType === SlotTypeEnum.SHOOT ||
      !t.slotType
    );
  });
}

function appendLocationVisibilityPatchesForDayOrder(
  orderedIds: string[],
  slotMap: Map<string, SlotInfo>,
  getSceneLocation: (sceneID: string | undefined) => string,
  patches: SlotLocationVisibilityPatch[],
): void {
  let prevNormAddr: string | null = null;
  let anchorSolt: SlotInfo | null = null;

  for (const id of orderedIds) {
    const slot = slotMap.get(id);
    if (!slot) continue;

    if (isNoteSlotForLocationDedupe(slot)) {
      prevNormAddr = null;
      anchorSolt = null;
      continue;
    }

    const normAddr = (getSceneLocation(slot.slotBelongSceneID) ?? '').trim();

    const want: 'Y' | 'N' =
      prevNormAddr !== null && normAddr === prevNormAddr ? 'N' : 'Y';
    if (want === 'Y') {
      prevNormAddr = normAddr;
      anchorSolt = slot;
    }

    const cur = slot.slotLocationVisibility;
    const needVisPatch =
      want === 'Y' ? !locationVisibilityStoredMeansY(cur) : !locationVisibilityStoredIsN(cur);
    const desiredShiftFromAnchor =
      want === 'N' && anchorSolt ? normalizedSlotShiftForDedupe(anchorSolt) : null;
    const needShiftPatch =
      want === 'N' &&
      anchorSolt !== null &&
      normalizedSlotShiftForDedupe(slot) !== desiredShiftFromAnchor;

    if (!needVisPatch && !needShiftPatch) continue;

    const patch: SlotLocationVisibilityPatch = {
      slotID: id,
      slotLocationVisibility: want,
    };
    if (needShiftPatch) {
      patch.slotShift = desiredShiftFromAnchor;
    }
    patches.push(patch);
  }
}

/**
 * 根据一份已经确定的日内扁平顺序计算地点头补丁。
 *
 * 跨日拖拽在 React 乐观状态提交前就已经得到源日、目标日的新顺序；此函数允许拖拽逻辑
 * 直接按该顺序确定地点边界，避免再从可能仍是旧值的 calDate/slot 日期快照反推。
 */
export function computeSlotLocationVisibilityPatchesForOrderedIds(
  orderedIds: string[],
  slots: SlotInfo[],
  getSceneLocation: (sceneID: string | undefined) => string,
): SlotLocationVisibilityPatch[] {
  const slotMap = new Map(slots.map((slot) => [slot.slotID, slot]));
  const patches: SlotLocationVisibilityPatch[] = [];
  appendLocationVisibilityPatchesForDayOrder(
    normalizeToSortedIds(orderedIds),
    slotMap,
    getSceneLocation,
    patches,
  );
  return patches;
}

export function computeSlotLocationVisibilityDedupePatches(
  scheduleID: string,
  calDates: CalDateInfo[],
  slots: SlotInfo[],
  getSceneLocation: (sceneID: string | undefined) => string,
): SlotLocationVisibilityPatch[] {
  const slotsForSchedule = slots.filter(
    (t) => !t.slotBelongScheduleID || t.slotBelongScheduleID === scheduleID,
  );
  const slotMap = new Map(slotsForSchedule.map((t) => [t.slotID, t]));
  const calDatesForSchedule = calDates.filter(
    (pd) => !pd.calDateBelongScheduleID || pd.calDateBelongScheduleID === scheduleID,
  );

  const patches: SlotLocationVisibilityPatch[] = [];

  for (const pd of calDatesForSchedule) {
    if (!pd.calDateDt) continue;
    const dateStr = pd.calDateDt.split('T')[0];

    const daySolts = filterDaySoltsForCalendar(slotsForSchedule, dateStr);
    const segments = buildDisplaySegments(
      daySolts,
      pd.calDateSlotsSort,
      dateStr,
      () => undefined,
    );
    const orderedIds = segments.flatMap((s) => s.items);
    appendLocationVisibilityPatchesForDayOrder(
      orderedIds,
      slotMap,
      getSceneLocation,
      patches,
    );
  }

  return patches;
}

/**
 * 仅对单日（dateStr=YYYY-MM-DD）执行与“按地点整理”一致的地点头显示归一化：
 * - 顺序以 calDateSlotsSort 为主，未包含的当日任务（orphans）追加在末尾（与 DayInfo 行顺序一致）
 * - 连续相同地点：首条 Y，其余 N；NOTE 打断连续段
 * - 仅返回相对当前 slot 状态需要写入的 patch（含必要时的 shift 对齐）
 */
export function computeSlotLocationVisibilityDedupePatchesForDay(
  scheduleID: string,
  dateStr: string,
  calDate: CalDateInfo | undefined,
  slots: SlotInfo[],
  getSceneLocation: (sceneID: string | undefined) => string,
): SlotLocationVisibilityPatch[] {
  if (!calDate?.calDateDt) return [];
  const pdDateStr = calDate.calDateDt.split('T')[0];
  if (pdDateStr !== dateStr) return [];

  const slotsForSchedule = slots.filter(
    (t) => !t.slotBelongScheduleID || t.slotBelongScheduleID === scheduleID,
  );
  const daySolts = filterDaySoltsForCalendar(slotsForSchedule, dateStr);
  if (daySolts.length === 0) return [];

  const slotMap = new Map(slotsForSchedule.map((t) => [t.slotID, t]));
  const segments = buildDisplaySegments(
    daySolts,
    calDate.calDateSlotsSort,
    dateStr,
    () => undefined,
  );
  const orderedIds = segments.flatMap((s) => s.items);

  const patches: SlotLocationVisibilityPatch[] = [];
  appendLocationVisibilityPatchesForDayOrder(orderedIds, slotMap, getSceneLocation, patches);
  return patches;
}
